import { useEffect, useRef, useState } from 'react';

const RECONNECT_MIN_MS = 2000;
const RECONNECT_MAX_MS = 30000;

/**
 * Підписка на Server-Sent Events з cookie-авторизацією і власним reconnect.
 *
 * Браузерний EventSource переподключається сам лише після «м'якого» розриву;
 * після HTTP-помилки (401/403/5xx) він переходить у CLOSED і мовчить — тому
 * при error закриваємо і відкриваємо заново з експоненційною паузою.
 *
 * EventSource не віддає HTTP-статус помилки, тому опційний `probe()` (звичайний fetch того ж
 * бекенду) з'ясовує причину: 401/403/мережа — і вона потрапляє в `error` для підказки користувачу.
 *
 * @param {string|null} url — null вимикає підписку
 * @param {Record<string, (data: any) => void>} handlers — { 'call.answered': fn, ... }
 * @param {{ probe?: () => Promise<unknown> }} [opts]
 * @returns {{ connected: boolean, error: { status: number, reachable?: boolean } | null }}
 */
export default function useSseEvents(url, handlers, opts = {}) {
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const probeRef = useRef(opts.probe);
  probeRef.current = opts.probe;
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!url) {
      setConnected(false);
      return undefined;
    }

    let es = null;
    let timer = null;
    let delay = RECONNECT_MIN_MS;
    let disposed = false;

    const open = () => {
      if (disposed) return;
      es = new EventSource(url, { withCredentials: true });

      es.onopen = () => {
        delay = RECONNECT_MIN_MS;
        setConnected(true);
        setError(null);
      };

      es.onerror = () => {
        setConnected(false);
        es.close();
        es = null;
        if (disposed) return;
        // З'ясовуємо причину звичайним запитом: { status: 403 } — сервер відхилив, { status: 0 } — мережа.
        // Успішний probe означає, що впав саме потік (проксі/мережа), а не доступ.
        probeRef.current?.()
          .then(() => { if (!disposed) setError({ status: 0, reachable: true }); })
          .catch((err) => { if (!disposed) setError({ status: Number(err?.status) || 0 }); });
        timer = setTimeout(open, delay);
        delay = Math.min(delay * 2, RECONNECT_MAX_MS);
      };

      // Іменовані події (event: X) не приходять в onmessage — підписуємось на кожну
      for (const name of Object.keys(handlersRef.current)) {
        es.addEventListener(name, (e) => {
          let data = null;
          try {
            data = JSON.parse(e.data);
          } catch {
            data = e.data;
          }
          handlersRef.current[name]?.(data);
        });
      }
    };

    open();

    return () => {
      disposed = true;
      clearTimeout(timer);
      es?.close();
      setConnected(false);
      setError(null);
    };
    // Набір імен подій вважаємо стабільним протягом життя підписки
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  return { connected, error };
}
